import { Injectable, computed, signal } from '@angular/core';
import { GlobalReportApiService } from './global-report-api.service';
import { ReportScopeService } from '../report-scope.service';
import { latestRequestTracker } from 'src/app/utils/reports/latest-request';
import { LocationWithSellers } from 'src/app/models/core/sales-manager/location-sales.model';
import {
  GroupedSalesRow,
  GroupedSalesTotals,
} from 'src/app/utils/reports/group-sales';
import {
  computeLocationsTotals,
  sellersByLocationToGroupedRows,
} from 'src/app/utils/reports/sellers-by-location.mapper';

const EMPTY_TOTALS: GroupedSalesTotals = {
  quota: 0,
  quota_paid: 0,
  quota_pay_at_door: 0,
  total_registered: 0,
  total_verified: 0,
  total: 0,
};

/**
 * State for the "Locales" sub-tab of Grupo de ventas. Loads all locations with
 * their sellers from the dedicated `sellers-by-location` endpoint (fast, scoped)
 * instead of deriving them client-side from the heavy `v2/reports` payload.
 * Locations per event are few, so everything is fetched in a single request.
 */
@Injectable({ providedIn: 'root' })
export class SalesByLocationService {
  private readonly _locations = signal<LocationWithSellers[]>([]);
  /** Scope `(rol, evento)` de lo cargado; `null` = nada cargado todavía. */
  private readonly _loadedKey = signal<string | null>(null);
  readonly loading = signal(false);
  /** Scope del request en vuelo; solo vale mientras `loading()`. */
  private _inFlightKey: string | null = null;
  private readonly _request = latestRequestTracker();

  readonly groups = computed<GroupedSalesRow[]>(() =>
    sellersByLocationToGroupedRows(this._locations())
  );
  readonly totals = computed<GroupedSalesTotals>(() =>
    computeLocationsTotals(this._locations())
  );
  readonly isEmpty = computed(
    () => this._loadedKey() !== null && this._locations().length === 0
  );

  constructor(
    private globalReportApi: GlobalReportApiService,
    private reportScope: ReportScopeService,
  ) {}

  /**
   * Loads once per `(role, event)`; no-op if that is already what is held or is
   * in flight. The backend narrows by role, so the same event seen with another
   * role is different data and must be refetched even with a request in flight.
   */
  loadIfNeeded(eventId: number | string): void {
    const key = this.reportScope.keyFor(eventId);
    const inFlight = this.loading() && this._inFlightKey === key;
    if (inFlight || this._loadedKey() === key) {
      return;
    }
    this.load(eventId);
  }

  load(eventId: number | string): void {
    const key = this.reportScope.keyFor(eventId);
    // Si cambia el rol con un request en vuelo, la respuesta vieja se descarta.
    const isLatest = this._request.start();
    this._inFlightKey = key;
    this.loading.set(true);

    this.globalReportApi.getSellersByLocation(eventId).subscribe({
      next: ({ data = [] }: { data?: LocationWithSellers[] }) => {
        if (!isLatest()) return;
        this._locations.set(data);
        this._loadedKey.set(key);
      },
      error: () => {
        if (!isLatest()) return;
        this._locations.set([]);
        this._loadedKey.set(key);
        this.loading.set(false);
      },
      complete: () => {
        if (isLatest()) this.loading.set(false);
      },
    });
  }

  reset(): void {
    this._locations.set([]);
    this._loadedKey.set(null);
    this.loading.set(false);
  }
}
